import { replaceDashWithEmptySpace } from '@percent/admin-dashboard/common/utility/replaceDashWithEmptySpace'
import { capitalizeString } from '@percent/admin-dashboard/common/utility/capitalizeString'

/**
 * A single breadcrumb: the human-readable {@link label} shown to the user and the
 * id-based {@link path} it links to. Display and link are kept separate on purpose
 * — a crumb can show an entity *name* while linking to its *id* route (NEX-657).
 * The last (current-page) crumb has no `path`.
 */
export type BreadcrumbItem = { label: string; path?: string }

type BreadcrumbOverride = {
  resolve: (segments: string[], locationPath?: string) => BreadcrumbItem[]
  requiresLocationPath?: boolean
}

const MAX_VISIBLE_BREADCRUMBS = 4

const ID_PATTERN = /[0-9_]/

function isId(segment: string): boolean {
  return ID_PATTERN.test(segment)
}

function humanizeSegment(segment: string): string {
  if (isId(segment)) return segment

  const label = segment.includes('-') ? replaceDashWithEmptySpace(segment) : segment

  return label.charAt(0).toUpperCase() + label.slice(1)
}

// A crumb's link is the real URL prefix, built from the actual path segments (which
// carry the ids) — never reconstructed from the humanized display labels. This is
// the fix for NEX-657: when a label is an entity/member name, slugifying it back
// into a path produced a broken, non-existent route.
const urlPrefix = (segments: string[], endIndex: number): string => `/${segments.slice(1, endIndex + 1).join('/')}`

// Shared finalisation: the root label is title-cased (every root is a single route
// word, e.g. "partners" → "Partners", so title-casing it is always safe), and the
// last crumb is the current page so it carries no link (falling back to the
// locationPath label when the trailing segment has no display text of its own).
function finalize(items: BreadcrumbItem[], locationPath?: string): BreadcrumbItem[] {
  const lastIndex = items.length - 1

  return items.map((item, i) => ({
    label: i === 0 ? capitalizeString(item.label) : i === lastIndex ? item.label || locationPath || '' : item.label,
    ...(i === lastIndex ? {} : { path: item.path })
  }))
}

function resolveMemberProfile(segments: string[], locationPath?: string): BreadcrumbItem[] {
  // pathname: /{root}/{entityId}/members/{accountId}; locationPath: "entityName|memberName" or "memberName".
  const root = segments[1]
  const parts = locationPath?.split('|')
  const hasEntity = parts && parts.length > 1
  const entityLabel = hasEntity ? parts[0] : segments[2]
  const memberLabel = hasEntity ? parts[1] : locationPath || segments[4]

  return [
    { label: humanizeSegment(root), path: `/${root}` },
    // Display the entity name, but link to its id route (not the name).
    { label: entityLabel, path: `/${root}/${segments[2]}` },
    { label: 'Members', path: `/${root}/${segments[2]}/members` },
    { label: memberLabel }
  ]
}

const overrides: Record<string, BreadcrumbOverride> = {
  'bank-details-reviews': {
    resolve: (segments, locationPath) => [
      { label: replaceDashWithEmptySpace(segments[1]), path: urlPrefix(segments, 1) },
      { label: locationPath! }
    ],
    requiresLocationPath: true
  },
  activity: {
    resolve: (segments, locationPath) => [
      { label: humanizeSegment(segments[1]), path: `/activity?status=${locationPath}` },
      { label: segments[2] }
    ],
    requiresLocationPath: true
  },
  eligibility: {
    resolve: (segments, locationPath) => [
      { label: humanizeSegment(segments[1]), path: urlPrefix(segments, 1) },
      { label: locationPath! }
    ],
    requiresLocationPath: true
  },
  disbursements: {
    resolve: (segments, locationPath) => {
      // A single-crumb page has no link (it's the current page).
      if (segments.length <= 2) return [{ label: humanizeSegment(segments[1]) }]

      const isDonationsPage = segments.length > 3 && segments[3] === 'donations'

      const items: BreadcrumbItem[] = [
        { label: humanizeSegment(segments[1]), path: urlPrefix(segments, 1) },
        // On the batch detail page show the batch name when one is set, falling back to the id.
        { label: isDonationsPage ? segments[2] : locationPath || segments[2], path: urlPrefix(segments, 2) }
      ]

      if (isDonationsPage) {
        items.push({ label: segments[4] })
      }

      return items
    }
  },
  partners: {
    resolve: (segments, locationPath) => {
      if (segments.length >= 5 && segments[3] === 'members') {
        return resolveMemberProfile(segments, locationPath)
      }

      return defaultResolve(segments)
    }
  },
  organizations: {
    resolve: (segments, locationPath) => {
      if (segments.length >= 5 && segments[3] === 'members') {
        return resolveMemberProfile(segments, locationPath)
      }

      return defaultResolve(segments)
    }
  },
  accounts: {
    resolve: (segments, locationPath) => {
      if (segments.length >= 4 && (segments[2] === 'members' || segments[2] === 'roles')) {
        // /accounts/{members|roles}/:id → [Goodstack, Members|Roles, name]. /accounts
        // redirects to the members list, so both crumbs link to real routes.
        return [
          { label: 'Goodstack', path: '/accounts' },
          { label: humanizeSegment(segments[2]), path: urlPrefix(segments, 2) },
          { label: locationPath || segments[3] }
        ]
      }

      if (segments.length >= 4 && segments[2] === 'services') {
        // /accounts/services/:serviceId[/roles] → [Accounts, Services, serviceName(, Roles)]
        const items: BreadcrumbItem[] = [
          { label: 'Accounts', path: '/accounts' },
          { label: 'Services', path: '/accounts/services' },
          { label: locationPath || segments[3], path: urlPrefix(segments, 3) }
        ]

        return segments[4] ? [...items, { label: humanizeSegment(segments[4]) }] : items
      }

      if (segments.length >= 4 && segments[2] === 'general') {
        // /accounts/general/:accountId → [Accounts, accountName]. These are not
        // Goodstack accounts, so the crumb is neutral rather than "Goodstack".
        return [{ label: 'Accounts', path: '/accounts' }, { label: locationPath || segments[3] }]
      }

      return defaultResolve(segments)
    }
  }
}

export function resolveBreadcrumbs(pathname: string, locationPath?: string): BreadcrumbItem[] {
  const segments = pathname.split('/')
  const root = segments[1] ?? ''

  const override = overrides[root]

  const items =
    override && !(override.requiresLocationPath && !locationPath)
      ? override.resolve(segments, locationPath)
      : defaultResolve(segments)

  return finalize(items, locationPath)
}

function defaultResolve(segments: string[]): BreadcrumbItem[] {
  return segments.slice(1, MAX_VISIBLE_BREADCRUMBS + 1).map((segment, i) => ({
    label: i === 0 ? (segment.includes('-') ? replaceDashWithEmptySpace(segment) : segment) : humanizeSegment(segment),
    path: urlPrefix(segments, i + 1)
  }))
}
